import { Link } from "react-router";
import { ArrowRight, Grid3x3Icon } from "lucide-react";
import { Card } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { CreateMatrixDialog } from "@/features/matrix";
import { RecordOwnerDisplay } from "@/domains/record-owners/components";
import { formatRelativeTime } from "@/shared/lib/utils";
import type { DashboardMatrixItem } from "../queries/dashboard";
import type { DashboardSectionView } from "../hooks/use-dashboard-view-preferences";
import { DashboardCarousel } from "./dashboard-carousel";
import { DashboardSectionViewToggle } from "./dashboard-section-view-toggle";

type DashboardMatricesSectionProps = {
  items: DashboardMatrixItem[];
  view: DashboardSectionView;
  onViewChange: (value: DashboardSectionView) => void;
};

export function DashboardMatricesSection({
  items,
  view,
  onViewChange,
}: DashboardMatricesSectionProps) {
  return (
    <Card
      role="region"
      aria-label="Matrices"
      className="overflow-hidden border-border/60 bg-card/95 shadow-xs"
    >
      <div className="flex flex-col gap-4 border-b border-border/70 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            Matrices
          </h2>
          <p className="text-sm text-muted-foreground">Most recently updated</p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <DashboardSectionViewToggle value={view} onChange={onViewChange} />
          <CreateMatrixDialog
            trigger={
              <Button className="rounded-xl px-4">
                <Grid3x3Icon className="size-4" />
                Create matrix
              </Button>
            }
          />
          <Button
            asChild
            variant="ghost"
            className="rounded-xl px-2 text-primary"
          >
            <Link to="/matrix">
              View all
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-start gap-4 px-6 py-8">
          <div className="rounded-2xl border border-dashed border-border/80 bg-muted/20 p-4 text-muted-foreground">
            No matrices yet.
          </div>
        </div>
      ) : view === "table" ? (
        <div className="overflow-x-auto px-3 py-2">
          <table className="min-w-full border-separate border-spacing-y-2">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Rows</th>
                <th className="px-3 py-2 font-medium">Columns</th>
                <th className="px-3 py-2 font-medium">Updated</th>
                <th className="px-3 py-2 font-medium">Created by</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="rounded-2xl border border-border/60 bg-background transition-colors hover:bg-muted/30"
                >
                  <td className="rounded-l-2xl px-3 py-3 text-sm font-medium text-foreground">
                    <Link to={`/matrix/${item.id}`} className="hover:underline">
                      {item.name}
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-foreground">
                    {item.rowCount}
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-foreground">
                    {item.columnCount}
                  </td>
                  <td className="px-3 py-3 text-sm text-muted-foreground">
                    {formatRelativeTime(item.updatedAt)}
                  </td>
                  <td className="rounded-r-2xl px-3 py-3">
                    <RecordOwnerDisplay owner={item.owner} compact />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <DashboardCarousel label="Matrices">
          {items.map((item) => (
            <Card
              key={item.id}
              className="flex min-h-56 min-w-0 flex-col justify-between rounded-3xl border-border/70 bg-linear-to-br from-background to-muted/30 p-5"
            >
              <div className="space-y-4">
                <div>
                  <p className="line-clamp-2 text-lg font-semibold text-foreground">
                    <Link to={`/matrix/${item.id}`} className="hover:underline">
                      {item.name}
                    </Link>
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {item.rowCount} {item.rowCount === 1 ? "row" : "rows"} •{" "}
                    {item.columnCount}{" "}
                    {item.columnCount === 1 ? "column" : "columns"}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Updated {formatRelativeTime(item.updatedAt)}
                  </p>
                </div>
                <RecordOwnerDisplay owner={item.owner} />
              </div>

              <Button asChild className="mt-6 w-full rounded-xl">
                <Link
                  to={`/matrix/${item.id}`}
                  aria-label={`Open ${item.name}`}
                >
                  Open
                </Link>
              </Button>
            </Card>
          ))}
        </DashboardCarousel>
      )}
    </Card>
  );
}
